import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TodoDto,
  TodoExternalItemTypeDto,
  TodoTypeDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { IconLibrary } from '@act/storybook';
import { docketActions, selectTabPreferences } from 'redux/slices/docketSlice';
import { todosAppSelectors } from 'redux/slices/todoSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { hasPermission } from 'utilities/hasPermissions';
import { MenuItemConfig } from 'toolkit/tables/types/menu.types';
import { PaginationBounds } from 'toolkit/tables/types/table.types';
import { Order } from 'react-components/member-index/sortable-table-headers';
import { getFollowUpEventComparator } from 'react-components/docket/components/utilities/sortFollowUpUtils';
import { updateCallNoFollowUpNeeded } from 'redux/slices/callsSlice';
import { updateMessageNoFollowUpNeeded } from 'redux/slices/messagesSlice';
import { callsRequests } from 'redux/requests/calls.request';
import { makeRequest } from 'redux/utils/api-request';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { useMobile } from '@act/utilities/mobile-context';
import { DEFAULT_ROWS_PER_PAGE_OPTIONS as rowsPerPageOptions } from 'react-components/docket/components/ScrollableTable';

// --- Type Definitions (Keep these local to the component structure) ---
export type FOLLOW_UP_HEADERS =
  | 'id'
  | 'type'
  | 'requiredAction'
  | 'createdAt'
  | 'status';

export interface TodoDtoWithMember extends TodoDto {
  member: UserDto;
}

export interface FollowUpEventsHookData {
  paginatedEvents: TodoDtoWithMember[];
  filteredEventsCount: number;
  bounds: PaginationBounds;
  menuItems: MenuItemConfig<TodoDtoWithMember>[];
  canManageMemberFeedback: boolean;

  // Handlers
  setBounds: React.Dispatch<React.SetStateAction<PaginationBounds>>;
  setSelectedFilters: (newValue: TodoExternalItemTypeDto[]) => void;
  handleTableChange: (changes: {
    orderBy: FOLLOW_UP_HEADERS;
    orderDirection: Order;
    pageSize: number;
    pageStart: number;
  }) => void;
}

const CACHED_TAB_KEY: string = 'follow-up-events';

// --- Custom Hook Implementation ---

export const useFollowUpEventsData = (
  guideId: string
): FollowUpEventsHookData => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigation();
  const { isMobile } = useMobile();

  // --- 1. Base State Management ---
  const [selectedFilters, _setSelectedFilters] = useState<
    Partial<TodoExternalItemTypeDto>[]
  >([]);
  const [orderBy, setOrderBy] = useState<FOLLOW_UP_HEADERS>('createdAt');
  const [orderDirection, setOrderDirection] = useState<Order>('desc');

  const cachedBounds =
    useAppSelector(selectTabPreferences)?.tabBounds[CACHED_TAB_KEY];
  const [bounds, setBounds] = useState<PaginationBounds>(() => {
    if (cachedBounds) return cachedBounds;
    return {
      pageSize: rowsPerPageOptions[0],
      pageStart: 0,
    };
  });

  // --- 2. Redux Selectors and Hooks ---
  const actingUser = useAppSelector(state => state.actingUser)?.user;
  const canManageMemberFeedback = hasPermission(
    actingUser,
    'CAN_MANAGE_MEMBER_FEEDBACK'
  );
  const followUpEvents =
    todosAppSelectors.useSelectIncompleteTodosForFollowUp(guideId);
  const memberMap = membersAppSelectors.useSelectMembersMap();
  const interactionsDialogHelper = useInteractionsDialog();
  const openConfirmReturnCallDialog = useConfirmationAlertDialog();

  // --- 3. Side Effects (Persistence and Responsiveness) ---

  // Save bounds to Redux for persistence
  useEffect(() => {
    void dispatch(
      docketActions.setDocketTabBounds({ tab: CACHED_TAB_KEY, bounds })
    );
  }, [bounds, dispatch]);

  // Adjust page size for mobile
  useEffect(() => {
    if (isMobile && bounds.pageSize !== 10) {
      setBounds(prevBounds => ({
        ...prevBounds,
        pageSize: 10,
        pageStart: 0,
      }));
    }
  }, [isMobile, bounds.pageSize]);

  // --- 4. Business Logic Handlers (Actions) ---

  // Extracted helper for the 'Call now' menu item
  const createReturnCall = useCallback(
    async (data: TodoDtoWithMember) => {
      // ... (Original createReturnCall logic)
      if (data?.feedbackCall) {
        const externalPhoneNumber = data.feedbackCall?.incoming
          ? data.feedbackCall?.originator?.phoneNumber
          : data.feedbackCall?.receiver?.phoneNumber;
        const shouldReturnCall = await openConfirmReturnCallDialog({
          title: `Return feedback call to ${data.member?.name}`,
          message: `Do you want to return a phone call to ${externalPhoneNumber}?`,
          icon: <IconLibrary iconName="Phone" size={24} weight="regular" />,
        });
        if (shouldReturnCall) {
          const returnedCall = await makeRequest(callsRequests.returnCall, {
            params: null,
            body: {
              callId: data.feedbackCall.id,
            },
          });
          interactionsDialogHelper.editCall({
            user: data.member,
            call: returnedCall,
          });
        }
      } else {
        interactionsDialogHelper.createCall({
          user: data.member,
        });
      }
    },
    [interactionsDialogHelper, openConfirmReturnCallDialog]
  );

  // Memoized Menu Items
  const menuItems: MenuItemConfig<TodoDtoWithMember>[] = useMemo(
    () => [
      // ... (Rest of your original menuItems array)
      {
        display: 'Call now',
        callback: createReturnCall,
        hidden: (data: TodoDtoWithMember) => {
          return !(
            data?.type === TodoTypeDto.CALL_BACK ||
            data?.type === TodoTypeDto.FEEDBACK_CALL
          );
        },
      },
      {
        display: 'Text now',
        callback: (data: TodoDtoWithMember) =>
          interactionsDialogHelper.createMessage({
            user: data.member,
            phoneNumber: data.message.smsPhoneNumber,
          }),
        hidden: (data: TodoDtoWithMember) => {
          return data?.type !== TodoTypeDto.TEXT_BACK;
        },
      },
      {
        display: 'No action needed',
        callback: (data: TodoDtoWithMember) => {
          if (
            [TodoTypeDto.CALL_BACK, TodoTypeDto.FEEDBACK_CALL].includes(
              data?.type
            )
          ) {
            dispatch(
              updateCallNoFollowUpNeeded({
                params: {
                  callId:
                    data?.type === TodoTypeDto.FEEDBACK_CALL
                      ? data?.feedbackCall?.id
                      : data?.call.id,
                },
                body: { noFollowUpNeeded: true },
              })
            );
          }

          if (data?.type === TodoTypeDto.TEXT_BACK) {
            dispatch(
              updateMessageNoFollowUpNeeded({
                params: { messageId: data?.message.id },
                body: { noFollowUpNeeded: true },
              })
            );
          }
        },
        hidden: (data: TodoDtoWithMember) => {
          return ![
            TodoTypeDto.CALL_BACK,
            TodoTypeDto.FEEDBACK_CALL,
            TodoTypeDto.TEXT_BACK,
          ].includes(data?.type);
        },
      },
      // ... (Other menu items: Edit, Review call)
      {
        display: 'Edit',
        callback: (data: TodoDtoWithMember) => {
          if (
            data?.type === TodoTypeDto.INCOMPLETE_CALL ||
            data?.type === TodoTypeDto.INCOMPLETE_NOTE_CALL ||
            data?.type === TodoTypeDto.FEEDBACK_CALL
          ) {
            interactionsDialogHelper.editCall({
              call: data.call,
              user: memberMap[data.subjectId],
            });
          }

          if (
            data?.type === TodoTypeDto.INCOMPLETE_VISIT ||
            data?.type === TodoTypeDto.INCOMPLETE_NOTE_VISIT
          ) {
            interactionsDialogHelper.editVisit({
              visit: data.visit,
              userId: data.subjectId,
            });
          }
        },
        hidden: (data: TodoDtoWithMember) => {
          return [
            TodoTypeDto.CALL_BACK,
            TodoTypeDto.TEXT_BACK,
            TodoTypeDto.CALL_AUDIT,
          ].includes(data?.type);
        },
      },
      {
        display: 'Review call',
        callback: (data: TodoDtoWithMember) => {
          if (data?.type === TodoTypeDto.CALL_AUDIT) {
            navigate.toCalls({ onlyUnassignedCalls: true });
          }
        },
        hidden: (data: TodoDtoWithMember) => {
          return ![TodoTypeDto.CALL_AUDIT].includes(data?.type);
        },
      },
    ],
    [dispatch, interactionsDialogHelper, createReturnCall, memberMap, navigate]
  );

  // --- 5. Filtering, Sorting, and Pagination Logic (Core Data Transformation) ---

  const filteredAndSortedEvents = useMemo(() => {
    // 1. Combine Todos with Member data
    const eventsWithMembers = followUpEvents.map(e => ({
      ...e,
      member: memberMap[e.subjectId],
    }));

    // 2. Filter
    const filteredEvents = selectedFilters.length
      ? eventsWithMembers.filter(e =>
          selectedFilters.includes(e.externalItemType)
        )
      : eventsWithMembers;

    // 3. Sort
    const comparator = getFollowUpEventComparator(orderDirection, orderBy);
    return [...filteredEvents].sort(comparator);
  }, [followUpEvents, selectedFilters, memberMap, orderDirection, orderBy]);

  const paginatedEvents = useMemo(() => {
    const start = bounds.pageStart * bounds.pageSize;
    const end = start + bounds.pageSize;
    return filteredAndSortedEvents?.slice(start, end) || [];
  }, [bounds, filteredAndSortedEvents]);

  // --- 6. View/Table Handlers ---

  // Unified handler for sort/pagination changes from the table
  const handleTableChange = useCallback(
    ({ orderBy, orderDirection, pageSize, pageStart }) => {
      setOrderBy(orderBy);
      setOrderDirection(orderDirection);
      setBounds({ pageSize, pageStart });
    },
    []
  );

  // Handler for filter chip changes
  const setSelectedFilters = useCallback(
    (newValue: TodoExternalItemTypeDto[]) => {
      _setSelectedFilters(newValue);
      // Reset to page 1 on filter change
      setBounds(prevBounds => ({ ...prevBounds, pageStart: 0 }));
    },
    []
  );

  // Note: The two original `useEffect`s that called `handleFiltering` are now replaced
  // entirely by the `useMemo` hooks above, which automatically re-run when
  // `followUpEvents`, `selectedFilters`, `orderBy`, etc., change.

  // --- 7. Return Value ---
  return {
    paginatedEvents,
    filteredEventsCount: filteredAndSortedEvents?.length || 0,
    bounds,
    setBounds, // Still returned, as mobile pagination needs to directly update it
    menuItems,
    canManageMemberFeedback,
    setSelectedFilters,
    handleTableChange,
  };
};
